import { useEffect, useMemo, useRef, useState } from 'react';
import {
  omsOrderListSchema,
  omsPositionListSchema,
  type OmsOrder,
  type OmsOrderList,
  type OmsPosition,
  type OmsPositionList,
} from '@hf/sharedContract';
import { useHttp } from '@core/utils/http';

export type MonitorOrders = {
  /** Null until the first read for this trade day lands. */
  orders: OmsOrder[] | null;
  failed: boolean;
  /** Signed lots filled on the trade day, by symbol, from GET /oms/positions. Null until the first read, or when that first read fails. */
  positions: ReadonlyMap<string, number> | null;
  /** The same positions read, for the Positions grid. A later failed refresh keeps this list and sets positionsFailed. */
  positionList: OmsPosition[] | null;
  positionsFailed: boolean;
  /** Last successful order read. */
  asOf: string | null;
  /** The longer of the order-read and positions-read failure streaks. Three in a row is offline. */
  failures: number;
};

/**
 * The blotter and the Positions grid share these reads: one order list and one positions list per tick.
 * The same positions route in flight twice would abort the first call, so the grid must not read it again.
 * A later tick for the same day keeps the last figures on screen until the next read lands.
 */
export function useMonitorOrders(date: string | undefined, refreshTick: number, enabled: boolean): MonitorOrders {
  const { request } = useHttp();
  const [orders, setOrders] = useState<OmsOrder[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [positionList, setPositionList] = useState<OmsPosition[] | null>(null);
  const [positionsFailed, setPositionsFailed] = useState(false);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [orderFailures, setOrderFailures] = useState(0);
  const [positionFailures, setPositionFailures] = useState(0);
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const key = date ?? '';
    const background = loadedFor.current === key;
    if (!background) {
      setOrders(null);
      setFailed(false);
      setPositionList(null);
      setPositionsFailed(false);
    }
    const query = date ? `?date=${date}` : '';
    void request<OmsOrderList>(`/oms/orders${query}`, { responseSchema: omsOrderListSchema }).then((result) => {
      if (cancelled) return;
      loadedFor.current = key;
      if (result.status !== 200) {
        setOrderFailures((count) => count + 1);
        if (!background) {
          setOrders([]);
          setFailed(true);
        }
        return;
      }
      setOrders(result.data && Array.isArray(result.data.orders) ? result.data.orders : []);
      setFailed(false);
      setAsOf(new Date().toISOString());
      setOrderFailures(0);
    });
    void request<OmsPositionList>(`/oms/positions${query}`, { responseSchema: omsPositionListSchema }).then((result) => {
      if (cancelled) return;
      if (result.status !== 200 || !result.data || !Array.isArray(result.data.positions)) {
        setPositionFailures((count) => count + 1);
        setPositionsFailed(true);
        if (!background) setPositionList(null);
        return;
      }
      setPositionList(result.data.positions);
      setPositionsFailed(false);
      setPositionFailures(0);
    });
    return () => {
      cancelled = true;
    };
  }, [date, enabled, refreshTick, request]);

  const positions = useMemo(
    () => (positionList ? new Map(positionList.map((position): [string, number] => [position.bbFullSymbol, position.filled])) : null),
    [positionList],
  );

  return { orders, failed, positions, positionList, positionsFailed, asOf, failures: Math.max(orderFailures, positionFailures) };
}
